import React, { useState, useEffect } from 'react';
import { referralsService, ReferralRequest, EmployeeRating } from '../../services/referrals.service';
import { initializeMockReferralRequests } from '../../data/mockReferralRequests';
import CandidateRating from './CandidateRating';
import CandidateProfileModal from './CandidateProfileModal';
import ChatInterface from './ChatInterface';
import './ReferralRequests.css';

interface ReferralRequestsProps {
  userId: string;
  userCompanyId?: string;
  userCompanyName?: string;
  userName?: string;
  darkMode?: boolean;
}

const ReferralRequests: React.FC<ReferralRequestsProps> = ({ 
  userId, 
  userCompanyId,
  userCompanyName,
  userName = 'Employee',
  darkMode = false 
}) => {
  const [requests, setRequests] = useState<ReferralRequest[]>([]);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pending: 0,
    accepted: 0,
    declined: 0
  });
  const [employeeRating, setEmployeeRating] = useState<EmployeeRating | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'declined'>('all');
  const [showDeclineModal, setShowDeclineModal] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<ReferralRequest | null>(null);
  const [chatRequest, setChatRequest] = useState<ReferralRequest | null>(null);

  useEffect(() => {
    // Initialize mock data for demonstration
    if (userId) {
      initializeMockReferralRequests(userId);
    }
    loadReferralRequests();
    loadEmployeeRating();
  }, [userId]);

  const loadReferralRequests = async () => {
    setLoading(true);
    try {
      const reqs = await referralsService.getReferralRequests(userId, userCompanyId);
      setRequests(reqs);
      
      const stats = await referralsService.getReferralStats(userId);
      setStats({
        totalRequests: stats.totalRequests,
        pending: stats.pending,
        accepted: stats.accepted,
        declined: stats.declined
      });
    } catch (error) {
      console.error('Error loading referral requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeRating = async () => {
    const rating = await referralsService.getEmployeeRating(userId);
    setEmployeeRating(rating);
  };

  const handleAccept = async (requestId: string) => {
    const updated = await referralsService.updateReferralRequestStatus(
      requestId,
      userId,
      'accepted'
    );
    if (updated) {
      await loadReferralRequests();
      await loadEmployeeRating();
    }
  };

  const handleDecline = async (requestId: string) => {
    const updated = await referralsService.updateReferralRequestStatus(
      requestId,
      userId,
      'declined',
      declineReason
    );
    if (updated) {
      await loadReferralRequests();
      await loadEmployeeRating();
      setShowDeclineModal(null);
      setDeclineReason('');
    }
  };

  const handleChat = async (requestId: string) => {
    const request = requests.find(r => r.id === requestId);
    if (request) {
      setChatRequest(request);
    }
  };
  
  const handleContinueChat = async (requestId: string) => {
    const request = requests.find(r => r.id === requestId);
    if (request) {
      setChatRequest(request);
    }
  };

  const getFilteredRequests = () => {
    if (filter === 'all') return requests;
    return requests.filter(req => req.status === filter);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const getAvatarUrl = (name: string, email: string) => {
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${email || name}`;
  };

  if (loading) {
    return <div className="referral-loading">Loading referral requests...</div>;
  }

  return (
    <div className={`referral-requests-container ${darkMode ? 'dark' : 'light'}`}>
      {/* Employee Rating Section */}
      {employeeRating && employeeRating.totalProcessed > 0 && (
        <div className="employee-rating-section">
          <h3>Your Referral Performance</h3>
          <div className="rating-stats">
            <div className="stat-item">
              <div className="stat-value">{employeeRating.acceptanceRate.toFixed(0)}%</div>
              <div className="stat-label">Acceptance Rate</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{employeeRating.totalProcessed}</div>
              <div className="stat-label">Total Processed</div>
            </div>
            {employeeRating.toxicityScore > 0 && (
              <div className="stat-item warning">
                <div className="stat-value">{employeeRating.toxicityScore}</div>
                <div className="stat-label">Warning Score</div>
                {employeeRating.consecutiveDeclines >= 5 && (
                  <div className="warning-message">
                    ⚠️ High consecutive declines detected
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Dashboard */}
      <div className="referral-stats-dashboard">
        <div className="stat-card">
          <div className="stat-value">{stats.totalRequests}</div>
          <div className="stat-label">Total Requests</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.accepted}</div>
          <div className="stat-label">Accepted</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.declined}</div>
          <div className="stat-label">Declined</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({requests.length})
        </button>
        <button 
          className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({stats.pending})
        </button>
        <button 
          className={`filter-tab ${filter === 'accepted' ? 'active' : ''}`}
          onClick={() => setFilter('accepted')}
        >
          Accepted ({stats.accepted})
        </button>
        <button 
          className={`filter-tab ${filter === 'declined' ? 'active' : ''}`}
          onClick={() => setFilter('declined')}
        >
          Declined ({stats.declined})
        </button>
      </div>

      {/* Requests List */}
      <div className="requests-list">
        <h3>Referral Requests</h3>
        {getFilteredRequests().length === 0 ? (
          <div className="empty-state">
            <p>No {filter !== 'all' ? filter : ''} referral requests</p>
          </div>
        ) : (
          getFilteredRequests().map((request) => (
            <div key={request.id} className={`request-card ${request.status}`}>
              <div className="request-header">
                <div className="candidate-info">
                  <img 
                    alt={request.candidateName}
                    className="candidate-avatar"
                    src={request.candidateAvatar || getAvatarUrl(request.candidateName, request.candidateEmail)}
                  />
                  <div 
                    className="candidate-details"
                    onClick={() => setSelectedCandidate(request)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h4>{request.candidateName}</h4>
                    <p>{request.candidateTitle} • {request.candidateExperience}</p>
                  </div>
                </div>
                <div className="header-right">
                  <CandidateRating 
                    candidateId={request.candidateId} 
                    candidateName={request.candidateName}
                    darkMode={darkMode}
                  />
                  <div className={`status-badge ${request.status}`}>
                    {request.status}
                  </div>
                </div>
              </div>
              
              <div className="request-message">
                <p>{request.message}</p>
              </div>
              
              <div className="request-skills">
                {request.candidateSkills.map((skill, idx) => (
                  <span key={idx} className="skill-tag">{skill}</span>
                ))}
              </div>
              
              {request.status === 'declined' && request.declineReason && (
                <div className="decline-reason">
                  <strong>Decline reason:</strong> {request.declineReason}
                </div>
              )}
              
              <div className={`request-footer ${request.status === 'chat' ? 'chat-status' : ''}`}>
                <div className="request-time">
                  {formatDate(request.createdAt)}
                  {request.status === 'chat' && request.hasNewMessage && (
                    <span className="new-message-indicator">
                      <span className="material-icons">mark_email_unread</span>
                      New message
                    </span>
                  )}
                </div>
                <div className="request-actions">
                  {request.status === 'pending' && (
                    <>
                      <button 
                        className="action-btn accept"
                        onClick={() => handleAccept(request.id)}
                      >
                        Accept
                      </button>
                      <button 
                        className="action-btn decline"
                        onClick={() => setShowDeclineModal(request.id)}
                      >
                        Decline
                      </button>
                      <button 
                        className="action-btn chat"
                        onClick={() => handleChat(request.id)}
                      >
                        Chat
                      </button>
                    </>
                  )}
                  {request.status === 'accepted' && (
                    <span className="status-text accepted">✓ Accepted</span>
                  )}
                  {request.status === 'declined' && (
                    <span className="status-text declined">✗ Declined</span>
                  )}
                  {request.status === 'chat' && (
                    <>
                      <button 
                        className="action-btn accept"
                        onClick={() => handleAccept(request.id)}
                      >
                        Accept
                      </button>
                      <button 
                        className="action-btn decline"
                        onClick={() => setShowDeclineModal(request.id)}
                      >
                        Decline
                      </button>
                      <button 
                        className="action-btn chat"
                        onClick={() => handleChat(request.id)}
                      >
                        Chat
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="modal-overlay" onClick={() => setShowDeclineModal(null)}>
          <div className="decline-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reason for Declining</h3>
            <p className="modal-warning">
              Please provide a reason for declining this referral. 
              Remember: Excessive declines may affect your referral rating.
            </p>
            <textarea
              className="decline-reason-input"
              placeholder="Enter reason for declining (required)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowDeclineModal(null);
                  setDeclineReason('');
                }}
              >
                Cancel
              </button>
              <button 
                className="confirm-decline-btn"
                onClick={() => handleDecline(showDeclineModal)}
                disabled={!declineReason.trim()}
              >
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Candidate Profile Modal */}
      {selectedCandidate && (
        <CandidateProfileModal
          request={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
          darkMode={darkMode}
        />
      )}
      
      {/* Chat Modal */}
      {chatRequest && (
        <div className="modal-overlay" onClick={() => setChatRequest(null)}>
          <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
            <ChatInterface
              request={chatRequest}
              employeeId={userId}
              employeeName={userName}
              onClose={() => setChatRequest(null)}
              darkMode={darkMode}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralRequests;